/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getMonitorAgent,
  getMonitorAgentResourceHistory,
  MonitorAgent,
} from "@/api/monitor";
import { useMonitorAgent } from "@/hooks/useMonitorAgent";
import { formatBytes } from "@/utils/formatBytes";
import { MiniAreaChart } from "@/components/common/MiniAreaChart";
import { CHART_COLORS } from "@/constants/chartColors";
import { WidgetCard, WidgetAccent } from "./WidgetCard";

interface AgentResourcesWidgetProps {
  agentId: number;
}

export const AgentResourcesWidget: React.FC<AgentResourcesWidgetProps> = ({ agentId }) => {
  const { data: agent, isLoading: isLoadingAgent } = useQuery({
    queryKey: ["monitor-agent", agentId],
    queryFn: () => getMonitorAgent(agentId),
  });

  if (isLoadingAgent || !agent) {
    return (
      <WidgetCard title="Agent">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      </WidgetCard>
    );
  }

  // Split into an inner component so useMonitorAgent (which reads
  // agent.enabled/agent.id unconditionally) is never called with a
  // still-loading/undefined agent.
  return <AgentResourcesWidgetInner agent={agent} />;
};

const AgentResourcesWidgetInner: React.FC<{ agent: MonitorAgent }> = ({ agent }) => {
  const { hardwareStats, isLoadingHardwareStats } = useMonitorAgent({
    agent,
    includeHardwareStats: true,
  });

  const { data: history = [] } = useQuery({
    queryKey: ["monitor-agent-resource-history", agent.id],
    queryFn: () => getMonitorAgentResourceHistory(agent.id, 1),
  });

  const cpuHistory = history.map((p) => ({ time: p.createdAt, value: p.cpuUsagePercent }));

  const maxTemp = hardwareStats?.temperature?.length
    ? Math.max(...hardwareStats.temperature.map((t) => t.temperature))
    : undefined;
  const worstPercent = hardwareStats
    ? Math.max(
        hardwareStats.cpu.usage_percent,
        hardwareStats.memory.used_percent,
        hardwareStats.disks[0]?.used_percent ?? 0,
      )
    : 0;
  const accent: WidgetAccent = !hardwareStats
    ? "neutral"
    : worstPercent > 90 || (maxTemp ?? 0) > 80
      ? "error"
      : worstPercent > 70 || (maxTemp ?? 0) > 60
        ? "warn"
        : "ok";

  return (
    <WidgetCard title={agent.name} accent={accent}>
      {isLoadingHardwareStats || !hardwareStats ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading stats...</p>
      ) : (
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">CPU</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {hardwareStats.cpu.usage_percent.toFixed(0)}%
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Memory</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {hardwareStats.memory.used_percent.toFixed(0)}%
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Disk</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {hardwareStats.disks[0]
                ? `${hardwareStats.disks[0].used_percent.toFixed(0)}%`
                : "-"}
            </p>
          </div>
        </div>
      )}
      {hardwareStats?.disks[0] && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
          {formatBytes(hardwareStats.disks[0].used)} / {formatBytes(hardwareStats.disks[0].total)}
        </p>
      )}
      {hardwareStats?.temperature && hardwareStats.temperature.length > 0 && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">
          Max temp: {Math.max(...hardwareStats.temperature.map((t) => t.temperature)).toFixed(0)}°C
        </p>
      )}
      <div className="mt-2">
        <MiniAreaChart
          data={cpuHistory}
          color={CHART_COLORS.cpu}
          gradientId={`agent-cpu-${agent.id}`}
          height={70}
          valueFormatter={(v) => `${v.toFixed(0)}%`}
          emptyLabel="No history yet"
        />
      </div>
    </WidgetCard>
  );
};
